import React, { useState, useEffect } from 'react';
import {
  Users,
  Music,
  Timer,
  Calendar,
  Edit,
  Trash,
  AlertCircle
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import GlowingCard from '../ui/GlowingCard';

// Helper to format seconds -> mm:ss
const formatTime = (seconds) => {
  if (!seconds || Number.isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const RoutineCard = ({ routine, onEdit, onDelete, roster }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  // A simple subtle glow in dark mode (optional).
  const getDarkModeGlow = () => {
    return 'from-blue-600/10 via-purple-600/10 to-violet-600/10';
  };

  // Helper function to get user initials
  const getInitials = (firstName, lastName) =>
    `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""}`.toUpperCase();

  // Gather dancer details with photos
  const getDancerDetails = () => {
    if (!routine.dancers || !roster) return [];
    return routine.dancers
      .map(dancerId => roster.find(member => member.dancerId === dancerId))
      .filter(Boolean); // This already filters out deleted dancers
  };
  const dancers = getDancerDetails();

  return (
    <>
      {isDarkMode ? (
        <GlowingCard
          glowColor={getDarkModeGlow()}
          glowIntensity="blur-xl"
          cardOpacity="bg-[#1a1f2d]/90"
          className="p-5 group transition-all duration-200 hover:scale-[1.02] hover:shadow-2xl cursor-pointer"
        >
          <CardContent
            routine={routine}
            dancers={dancers}
            isDarkMode={isDarkMode}
            getInitials={getInitials}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </GlowingCard>
      ) : (
        <div className="bg-base-100 rounded-lg shadow-lg hover:shadow-2xl transition-all duration-200 hover:scale-[1.02] cursor-pointer group">
          <div className="p-5">
            <CardContent
              routine={routine}
              dancers={dancers}
              isDarkMode={isDarkMode}
              getInitials={getInitials}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          </div>
        </div>
      )}
    </>
  );
};

const CardContent = ({
  routine,
  dancers,
  isDarkMode,
  getInitials,
  onEdit,
  onDelete
}) => {
  const [audioDuration, setAudioDuration] = useState(null);
  const hasSong = Boolean(routine.songFileName);

  // If we have a songFileUrl, attempt to load it to measure duration
  useEffect(() => {
    if (!routine.songFileUrl) {
      setAudioDuration(null);
      return;
    }

    const audio = new Audio(routine.songFileUrl);

    const handleLoadedMetadata = () => {
      setAudioDuration(audio.duration);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    audio.addEventListener('error', () => {
      console.error("Error loading audio file for routine:", routine.songFileUrl);
      setAudioDuration(null);
    });

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.pause();
    };
  }, [routine.songFileUrl]);

  const displayTime = audioDuration ? formatTime(audioDuration) : '0:00';

  return (
    <div className={`${!hasSong ? `${isDarkMode ? 'bg-red-500/5' : 'bg-error/5'} rounded-lg -m-5 p-5` : ''}`}>
      {/* Header row: Title + Edit/Delete */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <h3
            className={`text-lg font-semibold truncate ${
              isDarkMode ? 'text-white' : 'text-base-content'
            } group-hover:text-primary transition-colors`}
          >
            {routine.title}
          </h3>
          {!hasSong && (
            <div className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
              isDarkMode ? 'bg-red-500/10 text-red-400' : 'bg-error/10 text-error'
            } group-hover:bg-opacity-100 transition-all`}>
              Missing Music
            </div>
          )}
        </div>
        <div className="relative flex-shrink-0">
          <div className="flex items-center space-x-2 opacity-80 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(routine);
              }}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? 'text-gray-400 hover:text-blue-400 hover:bg-white/5'
                  : 'text-base-content/70 hover:text-primary hover:bg-base-200'
              }`}
              title="Edit Routine"
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(routine);
              }}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? 'text-gray-400 hover:text-red-400 hover:bg-white/5'
                  : 'text-base-content/70 hover:text-error hover:bg-base-200'
              }`}
              title="Delete Routine"
            >
              <Trash className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Dancer Avatars - Always reserve space for consistent card sizing */}
      <div className="mb-4 h-10"> {/* Fixed height container */}
        {dancers.length > 0 ? (
          <div className="flex -space-x-3">
            {dancers.slice(0, 5).map((dancer, index) => (
              <div
                key={dancer.dancerId}
                className={`w-10 h-10 rounded-full border-2 ${
                  isDarkMode ? 'border-[#1a1f2d]' : 'border-white'
                }`}
                style={{
                  zIndex: dancers.length - index,
                  marginLeft: index > 0 ? '-0.75rem' : 0
                }}
              >
                {dancer.profilePicture ? (
                  <img
                    src={dancer.profilePicture}
                    alt={`${dancer.firstName} ${dancer.lastName}`}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <div
                    className={`w-full h-full rounded-full flex items-center justify-center text-xs font-medium ${
                      isDarkMode
                        ? 'bg-[#252D42] text-gray-300'
                        : 'bg-base-200'
                    }`}
                  >
                    {getInitials(dancer.firstName, dancer.lastName)}
                  </div>
                )}
              </div>
            ))}
            {dancers.length > 5 && (
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium border-2 ${
                  isDarkMode
                    ? 'bg-[#252D42] border-[#1a1f2d] text-gray-300'
                    : 'bg-base-200 border-white'
                }`}
                style={{ marginLeft: '-0.75rem', zIndex: 1 }}
              >
                +{dancers.length - 5}
              </div>
            )}
          </div>
        ) : (
          // Empty state placeholder - maintains consistent height
          <div className="flex items-center h-10">
            <div
              className={`w-10 h-10 rounded-full border-2 border-dashed flex items-center justify-center ${
                isDarkMode 
                  ? 'border-gray-600 bg-gray-700/20' 
                  : 'border-gray-300 bg-gray-100/50'
              }`}
            >
              <Users 
                className={`h-4 w-4 ${
                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                }`} 
              />
            </div>
            <span className={`ml-3 text-sm ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`}>
              No dancers assigned
            </span>
          </div>
        )}
      </div>

      {/* Info Grid: Time, Dancer Count, Audio, Average Age */}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          {/* LENGTH / TIME => measured audio duration */}
          <div
            className={`flex items-center text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-base-content/70'
            }`}
          >
            <Timer
              className={`h-4 w-4 mr-2 ${
                isDarkMode 
                  ? hasSong ? 'text-blue-400' : 'text-red-400'
                  : hasSong ? 'text-primary' : 'text-error'
              }`}
            />
            <span className={!hasSong ? `${
              isDarkMode ? 'text-red-400' : 'text-error'
            } font-medium` : ''}>
              {displayTime}
            </span>
          </div>

          {/* DANCER COUNT */}
          <div
            className={`flex items-center text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-base-content/70'
            }`}
          >
            <Users
              className={`h-4 w-4 mr-2 ${
                isDarkMode ? 'text-violet-400' : 'text-info'
              }`}
            />
            {dancers.length} Dancers
          </div>

          {/* SONG FILENAME (or No music) */}
          <div
            className={`flex items-center text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-base-content/70'
            } overflow-hidden whitespace-nowrap`}
            style={{ maxWidth: '100%' }}
          >
            {hasSong ? (
              <>
                <Music
                  className={`h-4 w-4 mr-2 ${
                    isDarkMode ? 'text-green-400' : 'text-success'
                  }`}
                />
                <span className="truncate">{routine.songFileName}</span>
              </>
            ) : (
              <div className={`flex items-center gap-2 ${
                isDarkMode ? 'text-red-400' : 'text-error'
              }`}>
                <AlertCircle className="h-4 w-4 mr-2" />
                <span className="truncate font-medium">No music added</span>
              </div>
            )}
          </div>

          {/* AVERAGE AGE (if applicable) */}
          <div
            className={`flex items-center text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-base-content/70'
            }`}
          >
            <Calendar
              className={`h-4 w-4 mr-2 ${
                isDarkMode ? 'text-yellow-400' : 'text-warning'
              }`}
            />
            Avg. Age {routine.averageAge || '-'}
          </div>
        </div>
      </div>

      {/* Next Event (if present) */}
      {routine.nextEvent && (
        <div
          className={`mt-4 pt-4 ${
            isDarkMode ? 'border-t border-white/5' : 'border-t border-base-200'
          }`}
        >
          <div className="flex justify-between items-center">
            <div className="text-sm">
              <span
                className={
                  isDarkMode ? 'text-gray-500' : 'text-base-content/60'
                }
              >
                Next Event:
              </span>
              <span
                className={`ml-2 font-medium ${
                  isDarkMode ? 'text-white' : 'text-base-content'
                }`}
              >
                {routine.nextEvent}
              </span>
            </div>
            <span
              className={
                isDarkMode ? 'text-xs text-gray-500' : 'text-xs text-base-content/60'
              }
            >
              {routine.eventDate}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutineCard;
